/**
 * Admin → Backups Dashboard
 * ──────────────────────────────────────────────────────────────────────────
 * Route: /admin/backups
 * Roles: super_admin only (path gated via rolePermissions.js wildcard)
 *
 * Sections:
 *  1. Status cards (Last backup / Next scheduled / Total runs / R2 status)
 *  2. "Run Backup Now" CTA → password confirmation modal
 *  3. Table of backup_logs (paged) with Download / Delete actions
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Database, Cloud, HardDrive, Clock, Download, Trash2, Play,
  RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2, Loader2,
} from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BASE_URL });
api.interceptors.request.use((c) => {
  const t =
    localStorage.getItem('su_token') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

function fmtBytes(mb) {
  if (mb == null) return '—';
  if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`;
  if (mb < 1024) return `${mb.toFixed(2)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
    });
  } catch {
    return iso;
  }
}

function fmtDuration(secs) {
  if (secs == null) return '—';
  if (secs < 60) return `${secs.toFixed(1)}s`;
  const m = Math.floor(secs / 60);
  const r = Math.round(secs % 60);
  return `${m}m ${r}s`;
}

function StatusBadge({ status }) {
  const map = {
    success: { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle2, label: 'Success' },
    running: { color: 'bg-[#D4A843]/10 text-[#F1E2B8] border-[#D4A843]/40', icon: Loader2, label: 'Running' },
    failed:  { color: 'bg-red-500/10 text-red-400 border-red-500/30', icon: AlertTriangle, label: 'Failed' },
  };
  const cfg = map[status] || { color: 'bg-white/5 text-white/60 border-white/10', icon: AlertTriangle, label: status };
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase border ${cfg.color}`}
    >
      <Icon size={12} className={status === 'running' ? 'animate-spin' : ''} />
      {cfg.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent = false, testId }) {
  return (
    <Card
      data-testid={testId}
      className={`p-5 border ${accent ? 'border-[#D4A843]/40 bg-[#1a1814]' : 'border-[#22221F] bg-[#101013]'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#9A9A92]">{label}</span>
        <span className={`w-8 h-8 rounded-full flex items-center justify-center border ${accent ? 'border-[#D4A843]/40 bg-[#0A0A0A]' : 'border-white/10 bg-[#1a1814]'}`}>
          <Icon size={14} className="text-[#D4A843]" />
        </span>
      </div>
      <div className="text-xl lg:text-2xl font-semibold tabular-nums text-white break-words">{value}</div>
      {sub && <div className="mt-1 text-xs text-[#7A7A72]">{sub}</div>}
    </Card>
  );
}

export default function BackupsPage() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runOpen, setRunOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [runSubmitting, setRunSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([
        api.get('/api/admin/backups/status'),
        api.get('/api/admin/backups', { params: { limit: 50, skip: 0 } }),
      ]);
      setStatus(s.data);
      setItems(list.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load backups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-refresh every 10s while a run is active
  useEffect(() => {
    if (!status?.running_count) return undefined;
    const t = setInterval(fetchAll, 10_000);
    return () => clearInterval(t);
  }, [status?.running_count, fetchAll]);

  const submitRun = async () => {
    if (!password || password.length < 6) {
      toast.error('Enter your Super Admin password');
      return;
    }
    setRunSubmitting(true);
    try {
      await api.post('/api/admin/backups/run', { password });
      toast.success('Backup started. Refresh in ~30 seconds.');
      setRunOpen(false);
      setPassword('');
      setTimeout(fetchAll, 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start backup');
    } finally {
      setRunSubmitting(false);
    }
  };

  const handleDownload = async (row) => {
    setDownloadingId(row.id);
    try {
      const r = await api.post(`/api/admin/backups/${row.id}/download-token`);
      const url = r.data.download_url || `${BASE_URL}/api/admin/backups/${row.id}/download?token=${r.data.token}`;
      // Open in same tab — browser handles the .tar.gz attachment naturally
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to issue download token');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await api.delete(`/api/admin/backups/${deleteTarget.id}`);
      const detail = [
        r.data.local_deleted ? 'local removed' : null,
        r.data.r2_deleted ? 'R2 removed' : null,
      ].filter(Boolean).join(', ') || 'log marked deleted';
      toast.success(`Backup deleted (${detail})`);
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete backup');
    }
  };

  const nextRun = status?.scheduler_jobs?.find((j) => j.id === 'backup_auto')?.next_run_time;
  const totalRuns = items.length;
  const lastSuccess = status?.last_success;
  const r2Enabled = !!status?.storage?.r2?.enabled;

  return (
    <AdminShell title="Data Backups">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h2 className="font-['EB_Garamond'] text-2xl text-white">Data Backup & Retention</h2>
            <p className="text-sm text-[#9A9A92] mt-1 max-w-2xl">
              Automatic snapshots of Tier-1 critical data every {status?.interval_days ?? 30} days,
              retained for {status?.retention_days ?? 180} days, mirrored to Cloudflare R2 from Day 1.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              data-testid="refresh-backups-btn"
              variant="outline"
              className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:text-white hover:bg-[#1A1A1A]"
              onClick={fetchAll}
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              <span className="ml-2">Refresh</span>
            </Button>
            <Button
              data-testid="run-backup-now-btn"
              className="bg-[#D4A843] hover:bg-[#c19938] text-[#0A0A0A] font-semibold"
              onClick={() => setRunOpen(true)}
              disabled={!!status?.running_count}
            >
              <Play size={14} />
              <span className="ml-2">{status?.running_count ? 'Backup running…' : 'Run Backup Now'}</span>
            </Button>
          </div>
        </div>

        {/* Status grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={CheckCircle2}
            label="Last Backup"
            value={lastSuccess ? fmtDateTime(lastSuccess.created_at) : 'None yet'}
            sub={lastSuccess ? `${fmtBytes(lastSuccess.size_mb)} · ${fmtDuration(lastSuccess.duration_secs)}` : 'Awaiting first run'}
            accent={!!lastSuccess}
            testId="stat-last-backup"
          />
          <StatCard
            icon={Clock}
            label="Next Scheduled"
            value={nextRun ? fmtDateTime(nextRun) : 'Scheduler off'}
            sub={`Every ${status?.interval_days ?? 30} days · 03:00 IST`}
            testId="stat-next-scheduled"
          />
          <StatCard
            icon={Database}
            label="Backups On Record"
            value={totalRuns}
            sub={`${status?.tier1_collections?.length ?? 10} Tier-1 collections`}
            testId="stat-total-backups"
          />
          <StatCard
            icon={Cloud}
            label="Cloudflare R2"
            value={r2Enabled ? 'Connected' : 'Disabled'}
            sub={r2Enabled ? status?.storage?.r2?.bucket : 'Set S3_* envs to enable'}
            accent={r2Enabled}
            testId="stat-r2-status"
          />
        </div>

        {/* Backups table */}
        <Card className="bg-[#101013] border-[#22221F] overflow-hidden" data-testid="backups-table-card">
          <div className="px-5 py-4 border-b border-[#22221F] flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white tracking-wide uppercase">
              Recent Backups
            </h3>
            <span className="text-xs text-[#7A7A72]">
              Showing latest {items.length} {items.length === 1 ? 'run' : 'runs'}
            </span>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-5 space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 bg-[#1A1A1A]" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center text-[#9A9A92]" data-testid="backups-empty-state">
                <Database size={28} className="mx-auto mb-3 text-[#D4A843]/60" />
                <p className="text-sm">No backups yet. Trigger the first manual run to verify connectivity.</p>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="backups-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-[#7A7A72] border-b border-[#22221F]">
                    <th className="px-5 py-3 font-medium">Created</th>
                    <th className="px-5 py-3 font-medium">Type</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Size</th>
                    <th className="px-5 py-3 font-medium">Duration</th>
                    <th className="px-5 py-3 font-medium">Storage</th>
                    <th className="px-5 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row.id}
                      className={`border-b border-[#1A1A1A] hover:bg-[#15151A] transition-colors ${row.deleted_at ? 'opacity-50' : ''}`}
                      data-testid={`backup-row-${row.id}`}
                    >
                      <td className="px-5 py-3 text-white tabular-nums whitespace-nowrap">
                        {fmtDateTime(row.created_at)}
                      </td>
                      <td className="px-5 py-3">
                        <Badge
                          variant="outline"
                          className={`text-[10px] tracking-wider uppercase ${row.type === 'manual' ? 'border-[#D4A843]/40 text-[#F1E2B8]' : 'border-white/15 text-white/70'}`}
                        >
                          {row.type}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        <StatusBadge status={row.status} />
                        {row.error && (
                          <div className="text-[11px] text-red-400 mt-1 max-w-xs truncate" title={row.error}>
                            {row.error}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-white tabular-nums">{fmtBytes(row.size_mb)}</td>
                      <td className="px-5 py-3 text-[#B9B9B9] tabular-nums">{fmtDuration(row.duration_secs)}</td>
                      <td className="px-5 py-3 text-xs">
                        <div className="flex items-center gap-2">
                          {row.local_key && (
                            <span className="inline-flex items-center gap-1 text-[#B9B9B9]" title="Local persistent disk">
                              <HardDrive size={12} /> local
                            </span>
                          )}
                          {row.r2_uploaded ? (
                            <span className="inline-flex items-center gap-1 text-emerald-400" title="Mirrored to Cloudflare R2">
                              <Cloud size={12} /> R2
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[#7A7A72]" title="Not on R2">
                              <Cloud size={12} /> —
                            </span>
                          )}
                          {row.deleted_at && (
                            <span className="inline-flex items-center gap-1 text-red-400/80" title={`Deleted ${fmtDateTime(row.deleted_at)}`}>
                              <Trash2 size={12} /> purged
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1.5">
                          <Button
                            data-testid={`download-backup-${row.id}`}
                            variant="ghost"
                            size="sm"
                            disabled={row.status !== 'success' || !!row.deleted_at || downloadingId === row.id}
                            onClick={() => handleDownload(row)}
                            className="h-8 text-[#D4A843] hover:text-[#F1E2B8] hover:bg-[#1A1A1A]"
                          >
                            {downloadingId === row.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Download size={14} />
                            )}
                            <span className="ml-1.5 text-xs">Download</span>
                          </Button>
                          <Button
                            data-testid={`delete-backup-${row.id}`}
                            variant="ghost"
                            size="sm"
                            disabled={row.status === 'running' || !!row.deleted_at}
                            onClick={() => setDeleteTarget(row)}
                            className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        {/* Footer disclaimer */}
        <div className="text-xs text-[#7A7A72] flex items-start gap-2 max-w-3xl">
          <ShieldCheck size={14} className="text-[#D4A843] mt-0.5 shrink-0" />
          <p>
            Tier-1 collections include {(status?.tier1_collections || []).join(', ') || 'bookings, payments, customers and more'}.
            Backups are stored unencrypted; rely on disk + R2 bucket encryption for at-rest protection.
            Download links are signed and expire after 5 minutes.
          </p>
        </div>
      </div>

      {/* Run-Now confirmation modal */}
      <Dialog open={runOpen} onOpenChange={(open) => { setRunOpen(open); if (!open) setPassword(''); }}>
        <DialogContent
          className="bg-[#101013] border-[#22221F] text-white max-w-md"
          data-testid="run-backup-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-['EB_Garamond'] text-xl flex items-center gap-2">
              <Play size={18} className="text-[#D4A843]" />
              Confirm Manual Backup
            </DialogTitle>
            <DialogDescription className="text-[#9A9A92] mt-2">
              Re-enter your Super Admin password to start a full backup of Tier-1 critical data.
              This may take 30–60 seconds and will be mirrored to Cloudflare R2 on completion.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              data-testid="run-backup-password-input"
              type="password"
              placeholder="Super Admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && password) submitRun(); }}
              className="bg-[#0A0A0A] border-[#22221F] text-white placeholder:text-[#5A5A52]"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setRunOpen(false)}
              className="text-[#B9B9B9] hover:text-white hover:bg-[#1A1A1A]"
              data-testid="run-backup-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitRun}
              disabled={runSubmitting || password.length < 6}
              className="bg-[#D4A843] hover:bg-[#c19938] text-[#0A0A0A] font-semibold"
              data-testid="run-backup-confirm-btn"
            >
              {runSubmitting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Play size={14} className="mr-2" />}
              Start Backup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent
          className="bg-[#101013] border-[#22221F] text-white"
          data-testid="delete-backup-modal"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['EB_Garamond'] text-xl flex items-center gap-2">
              <AlertTriangle size={18} className="text-red-400" />
              Delete this backup?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#9A9A92]">
              Created {deleteTarget && fmtDateTime(deleteTarget.created_at)} ·{' '}
              {deleteTarget && fmtBytes(deleteTarget.size_mb)}.
              <br />The archive will be removed from both <strong>local persistent disk</strong> and{' '}
              <strong>Cloudflare R2</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border-[#22221F] text-[#B9B9B9] hover:bg-[#1A1A1A] hover:text-white"
              data-testid="delete-backup-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="delete-backup-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
